import SiteHeader from '@/components/siteChrome/SiteHeader';
import SiteFooter from '@/components/siteChrome/SiteFooter';
import { Box, Breadcrumb, Link as ChakraLink, Flex, Text, VStack } from '@chakra-ui/react';
import React from 'react';
import { Outlet } from 'react-router-dom';
import Padding from '../../components/padding/Padding';

export interface LearnMoreSection {
  id: string;
  label: string;
}

export interface LearnMorePageMeta {
  breadcrumbLabel?: string;
}

export interface LearnMoreOutletContext {
  registerSections: (sections: LearnMoreSection[]) => void;
  clearSections: () => void;
  setPageMeta: (meta?: LearnMorePageMeta) => void;
}

const LearnMore = (): React.JSX.Element => {
  const [sections, setSections] = React.useState<LearnMoreSection[]>([]);
  const [pageMeta, setPageMeta] = React.useState<LearnMorePageMeta | undefined>();
  const [activeSectionId, setActiveSectionId] = React.useState<string | undefined>();

  const registerSections = React.useCallback((incoming: LearnMoreSection[] = []) => {
    setSections(incoming.filter(({ id, label }) => Boolean(id) && Boolean(label)));
  }, []);

  const clearSections = React.useCallback(() => {
    setSections([]);
  }, []);

  const setPageMetaFromChild = React.useCallback((meta?: LearnMorePageMeta) => {
    setPageMeta(meta);
  }, []);

  const updateActiveSection = React.useCallback(() => {
    if (typeof window === 'undefined') {
      return;
    }
    if (sections.length === 0) {
      setActiveSectionId(undefined);
      return;
    }
    const OFFSET = 160;
    let nextActiveId: string | undefined;
    let smallestDistance = Number.POSITIVE_INFINITY;

    sections.forEach(({ id }) => {
      const element = document.getElementById(id);
      if (!element) {
        return;
      }
      const rect = element.getBoundingClientRect();
      const distance = Math.abs(rect.top - OFFSET);
      if (distance < smallestDistance) {
        smallestDistance = distance;
        nextActiveId = id;
      }
    });

    setActiveSectionId(nextActiveId);
  }, [sections]);

  React.useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    if (sections.length === 0) {
      setActiveSectionId(undefined);
      return;
    }

    updateActiveSection();
    const handleScroll = () => updateActiveSection();
    window.addEventListener('scroll', handleScroll, { passive: true });
    window.addEventListener('resize', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleScroll);
    };
  }, [sections, updateActiveSection]);

  const outletContext = React.useMemo(
    () => ({
      registerSections,
      clearSections,
      setPageMeta: setPageMetaFromChild,
    }),
    [registerSections, clearSections, setPageMetaFromChild],
  );

  return (
    <Box>
      <SiteHeader />
      <Padding verticalPadding={12}>
        <Breadcrumb.Root size="md" marginBottom={{ base: 6, lg: 8 }}>
          <Breadcrumb.List>
            <Breadcrumb.Item>
              <Breadcrumb.Link href="/">Home</Breadcrumb.Link>
            </Breadcrumb.Item>
            <Breadcrumb.Separator />
            <Breadcrumb.Item>
              <Breadcrumb.Link href="/learn-more">Learn More</Breadcrumb.Link>
            </Breadcrumb.Item>
            <Breadcrumb.Separator />
            <Breadcrumb.Item>
              <Breadcrumb.CurrentLink>
                {pageMeta?.breadcrumbLabel ?? 'Details'}
              </Breadcrumb.CurrentLink>
            </Breadcrumb.Item>
          </Breadcrumb.List>
        </Breadcrumb.Root>
        <Flex direction={{ base: 'column', lg: 'row' }} gap={{ base: 8, lg: 12 }} align="stretch">
          <SideMenu
            sections={sections}
            activeSectionId={activeSectionId}
            onNavigate={setActiveSectionId}
          />
          <Box flex="3" order={{ base: 2, lg: 1 }} minW={0}>
            <Outlet context={outletContext} />
          </Box>
        </Flex>
      </Padding>
      <SiteFooter />
    </Box>
  );
};

interface SideMenuProps {
  sections: LearnMoreSection[];
  activeSectionId?: string;
  onNavigate: (id: string | undefined) => void;
}

const SideMenu = ({ sections, activeSectionId, onNavigate }: SideMenuProps): React.JSX.Element => {
  const handleNavigate = React.useCallback(
    (event: React.MouseEvent<HTMLAnchorElement>, id: string) => {
      event.preventDefault();
      const target = document.getElementById(id);
      if (target) {
        target.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
      if (typeof window !== 'undefined') {
        window.history.replaceState(null, '', `#${id}`);
      }
      onNavigate(id);
    },
    [onNavigate],
  );

  return (
    <Box
      className="hidden lg:block"
      order={{ base: 1, lg: 2 }}
      alignSelf={{ base: 'flex-end', lg: 'flex-start' }}
      position="sticky"
      top={{ base: 4, md: 28 }}
      width={{ base: '220px', md: '220px' }}
      px={5}
      py={0}
      zIndex={3}
      style={{
        transition: 'padding 0.3s ease-in-out',
      }}
    >
      <Text fontSize="sm" fontWeight="semibold">
        ON THIS PAGE
      </Text>
      <VStack align="stretch" gap={2} marginTop={4}>
        {sections.length === 0 && (
          <Text fontSize="sm" color="gray.400">
            Sections coming soon.
          </Text>
        )}
        {sections.map((section) => {
          const isActive = activeSectionId === section.id;
          return (
            <ChakraLink
              key={section.id}
              href={`#${section.id}`}
              fontSize="sm"
              fontWeight={isActive ? 'semibold' : 'light'}
              color={isActive ? 'blue.600' : 'gray.500'}
              _hover={{ color: 'blue.600', textDecoration: 'none' }}
              aria-current={isActive ? 'true' : undefined}
              onClick={(event) => handleNavigate(event, section.id)}
            >
              {section.label}
            </ChakraLink>
          );
        })}
      </VStack>
    </Box>
  );
};

export default LearnMore;
